import { getImage } from 'gatsby-plugin-image';
import React from 'react';
import { ArticleHeading } from './ArticleHeading/ArticleHeading';
import { ArticleInlineCallout } from './ArticleInlineCallout/ArticleInlineCallout';
import { ArticleLinks } from './ArticleLinks/ArticleLinks';
import { ArticleSourcesList } from './ArticleSourcesList/ArticleSourcesList';
import { AwardCard } from './Award/AwardCard/AwardCard';
import { AwardLink } from './Award/AwardLink/AwardLink';
import { CaseStudyPreview } from './CaseStudyPreview/CaseStudyPreview';
import { CityBlock } from './CityBlock/CityBlock';
import { ContactUs } from './ContactUs/ContactUs';
import { DataCards, DataCardsProps } from './DataCards/DataCards';
import { EmbeddedImage } from './EmbeddedImage/EmbeddedImage';
import { EmbeddedVideo } from './EmbeddedVideo/EmbeddedVideo';
import { ExternalForm } from './ExternalForm/ExternalForm';
import { FourColumn } from './FourColumn/FourColumn';
import {
    Infographic,
    InfographicProps,
    InfographicTabProps,
} from './Infographic/Infographic';
import { Quiz } from './Quiz/Quiz';
import { LogoModule } from './LogoModule/LogoModule';
import { NewsLetterSignUp } from './NewsLetterSignUp/NewsLetterSignUp';
import { QuoteText } from './QuoteText/QuoteText';
import { RawText } from './RawText/RawText';
import { RecommendedArticles } from './RecommendedArticles/RecommendedArticles';
import { SingleColumnDynamicForm } from './SingleColumnDynamicForm/SingleColumnDynamicForm';
import { singleColumnDynamicFormTransformer } from './SingleColumnDynamicForm/singleColumnDynamicFormTransformer';
import { SingleColumnText } from './SingleColumnText/SingleColumnText';
import { StepBlock } from './StepBlock/StepBlock';
import {
    ThreeColumnNarrow,
    ThreeColumnNarrowProps,
} from './ThreeColumnNarrow/ThreeColumnNarrow';
import { ThreeColumnNarrowColumnProps } from './ThreeColumnNarrow/ThreeColumnNarrowColumn';
import {
    ThreeColumnWide,
    ThreeColumnWideProps,
} from './ThreeColumnWide/ThreeColumnWide';
import { ThreeColumnWideColumnProps } from './ThreeColumnWide/ThreeColumnWideColumn';
import {
    TwoColumnCard,
    TwoColumnCardProps,
} from './TwoColumnCard/TwoColumnCard';
import { TwoColumnCardColumnProps } from './TwoColumnCard/TwoColumnCardColumn';
import { TwoColumnDynamicForm } from './TwoColumnDynamicForm/TwoColumnDynamicForm';
import { twoColumnDynamicFormTransformer } from './TwoColumnDynamicForm/twoColumnDynamicFormTransformer';
import {
    TwoColumnFormText,
    ITwoColumnFormText,
    ITwoColumnFormTextContext,
} from './TwoColumnFormText/TwoColumnFormText';
import { TwoColumnTextImage } from './TwoColumnTextImage/TwoColumnTextImage';
import { TwoColumnTextWMedia } from './TwoColumnTextMedia/TwoColumnTextMedia';
import { createQuizConfig } from './Quiz/QuizConfig';

export const getParagraph = (node, forceInline = false) => {
    if (node.type in PARAGRAPH_TYPES) {
        const paragraph = PARAGRAPH_TYPES[node.type];
        const Component = paragraph.component;

        if (!paragraph.dataTransformer) {
            return (
                <Component
                    key={node.id ? node.id : Math.random()}
                    node={node}
                    forceInline={forceInline}
                />
            );
        } else {
            return (
                <Component
                    key={node.id ? node.id : Math.random()}
                    forceInline={forceInline}
                    {...paragraph.dataTransformer(node)}
                />
            );
        }
    }
    return <p key={node.type}>Unknown type: {node.type}</p>;
};

export const PARAGRAPH_TYPES: {
    [machineName: string]: {
        component:
            | React.FC<{ node: any; forceInline?: boolean }>
            | ((props: any) => JSX.Element);
        dataTransformer?: (node: any) => any;
    };
} = {
    paragraph__article_links: { component: ArticleLinks },
    paragraph__award_primary_link: {
        component: ThreeColumnWide,
        dataTransformer: (node) =>
            ({
                columns: node.relationships?.field_featured_list?.map((col) => {
                    return {
                        image: getImage(
                            col.relationships?.field_image?.relationships
                                ?.field_media_image?.localFile
                        ),
                        imageAlt:
                            col.relationships?.field_image?.field_media_image
                                ?.alt,
                        link: col.field_link_single
                            ? {
                                  title: col.field_link_single.title,
                                  uri: col.field_link_single.uri,
                              }
                            : undefined,
                        imageTitle: col.relationships?.field_image?.name,
                        text: col.field_subtitle,
                        heading: col.field_heading,
                        variant: `featured-award`,
                    } as ThreeColumnWideColumnProps;
                }),
                theme: node.field_theme?.toLowerCase(),
                align: `left`,
                size: `small`,
                classNames: [`featured-awards-wrapper`],
            } as ThreeColumnWideProps),
    },
    paragraph__award_secondary_list: {
        component: AwardCard,
        dataTransformer: (node) => ({
            isDarkMode: node.field_dark_mode,
            awardCard: node.relationships?.field_card_list?.map((card) => ({
                heading: card.field_heading ? card.field_heading : ``,
                link: card.field_link_single
                    ? card.field_link_single
                    : {
                          tile: ``,
                          uri: ``,
                      },
                subtitle: card.field_subtitle ? card.field_subtitle : ``,
                image: card.relationships?.field_image?.relationships
                    ?.field_media_image?.localFile
                    ? getImage(
                          card.relationships?.field_image?.relationships
                              ?.field_media_image?.localFile
                      )
                    : undefined,
            })),
        }),
    },
    paragraph__award_tertiary_link: {
        component: AwardLink,
        dataTransformer: (node) => ({
            theme: node.field_theme,
            awardContext: node.relationships?.field_icon_text_link?.map(
                (award) => ({
                    contentText: award.field_content_text?.value,
                    link: award.field_link_single,
                    image: award.relationships?.field_icon_img
                        ? {
                              blueLine: getImage(
                                  award.relationships?.field_icon_img
                                      ?.relationships
                                      ?.field_image_dark_blue_line?.localFile
                              ),
                              whiteSticker: getImage(
                                  award.relationships?.field_icon_img
                                      ?.relationships?.field_image_white_sticker
                                      ?.localFile
                              ),
                              blueSticker: getImage(
                                  award.relationships?.field_icon_img
                                      ?.relationships
                                      ?.field_image_dark_blue_sticker?.localFile
                              ),
                          }
                        : undefined,
                })
            ),
        }),
    },
    paragraph__external_form: { component: ExternalForm },
    paragraph__two_column: {
        component: TwoColumnCard,
        dataTransformer: (node) =>
            ({
                columns: node.relationships?.field_two_column_columns.map(
                    (col) =>
                        ({
                            heading: col.field_heading,
                            eyebrow: col.field_eyebrow_text,
                            text: col.field_content_text?.value,
                            links: col.field_link,
                            image: getImage(
                                col.relationships?.field_image?.relationships
                                    ?.field_media_image?.localFile
                            ),
                            imageAlt:
                                col.relationships?.field_image
                                    ?.field_media_image?.alt,
                        } as TwoColumnCardColumnProps)
                ),
                darkMode: node.field_dark_mode,
                headline: node.field_heading,
            } as TwoColumnCardProps),
    },
    paragraph__single_column_dynamic_form: {
        component: SingleColumnDynamicForm,
        dataTransformer: singleColumnDynamicFormTransformer,
    },
    paragraph__single_column_text: {
        component: SingleColumnText,
    },
    paragraph__three_column: {
        component: ThreeColumnWide,
        dataTransformer: (node) =>
            ({
                columns: node.relationships?.field_three_column_columns?.map(
                    (col) => {
                        const embeddedThumbnail =
                            col.relationships?.field_media?.relationships
                                ?.field_remote_thumbnail?.localFile;
                        return {
                            image: getImage(
                                col.relationships?.field_media?.relationships
                                    ?.field_media_image?.localFile
                            ),
                            imageAlt:
                                col.relationships?.field_media
                                    ?.field_media_image?.alt,
                            link: col.field_link_single
                                ? {
                                      title: col.field_link_single.title,
                                      uri: col.field_link_single.uri,
                                  }
                                : undefined,
                            heading: col.field_heading,
                            title: col.relationships?.field_media?.name,
                            text: col.field_content_text?.value,
                            video: col.relationships?.field_media
                                ?.field_media_oembed_video,
                            aspectRatio:
                                col.relationships?.field_media
                                    ?.field_aspect_ratio,
                            thumbnail: embeddedThumbnail
                                ? getImage(embeddedThumbnail)
                                : ``,
                            thumbnailAlt:
                                col.relationships?.field_media?.thumbnail?.alt,
                        } as ThreeColumnWideColumnProps;
                    }
                ),
                theme: node.field_theme.toLowerCase(),
                align: node.field_lc_align.toLowerCase(),
                size: node.field_size_smlg.toLowerCase(),
            } as ThreeColumnWideProps),
    },
    paragraph__three_column_narrow: {
        component: ThreeColumnNarrow,
        dataTransformer: (node) =>
            ({
                columns: node.relationships?.field_three_column_columns?.map(
                    (col) =>
                        ({
                            heading: col.field_heading,
                            text: col.field_content_text?.value,
                            link: col.field_link_single
                                ? {
                                      title: col.field_link_single.title,
                                      uri: col.field_link_single.uri,
                                  }
                                : undefined,
                            image: col.relationships?.field_icon_img
                                ? {
                                      blueLine: getImage(
                                          col.relationships?.field_icon_img
                                              ?.relationships
                                              ?.field_image_dark_blue_line
                                              ?.localFile
                                      ),
                                      whiteSticker: getImage(
                                          col.relationships?.field_icon_img
                                              ?.relationships
                                              ?.field_image_white_sticker
                                              ?.localFile
                                      ),
                                      blueSticker: getImage(
                                          col.relationships?.field_icon_img
                                              ?.relationships
                                              ?.field_image_dark_blue_sticker
                                              ?.localFile
                                      ),
                                  }
                                : undefined,
                        } as ThreeColumnNarrowColumnProps)
                ),
                theme: node.field_theme?.toLowerCase(),
            } as ThreeColumnNarrowProps),
    },
    paragraph__four_column: { component: FourColumn },
    paragraph__logo: { component: LogoModule },
    paragraph__quote: { component: QuoteText },
    paragraph__text: { component: RawText },
    paragraph__article_heading: { component: ArticleHeading },
    paragraph__article_whitespace: {
        component: () => <div className="ws"></div>,
    },
    paragraph__article_inline_callout: { component: ArticleInlineCallout },
    paragraph__embedded_video: { component: EmbeddedVideo },
    paragraph__embedded_image: { component: EmbeddedImage },
    paragraph__header_body_collection: { component: StepBlock },
    paragraph__article_sources_list: { component: ArticleSourcesList },
    paragraph__quiz: {
        component: Quiz,
        dataTransformer: (node) => ({
            quizConfig: createQuizConfig(
                node?.relationships?.field_question_answer
            ),
        }),
    },
    paragraph__dynamic_case_study_preview: { component: CaseStudyPreview },
    paragraph__infographic: {
        component: Infographic,
        dataTransformer: (node) =>
            ({
                tabs: node.relationships?.field_infographic_tab?.map(
                    (tab) =>
                        ({
                            text: tab.field_content_text?.value,
                            heading: tab.field_heading,
                            tabTitle: tab.field_infographic_tab_title,
                            image: getImage(
                                tab.relationships?.field_image?.relationships
                                    ?.field_media_image?.localFile
                            ),
                            imageAlt:
                                tab.relationships?.field_image
                                    ?.field_media_image?.alt,
                        } as InfographicTabProps)
                ),
                percent: node.field_infographic_percent,
                infographicText: node.field_content_text?.value,
            } as InfographicProps),
    },
    paragraph__recommended_articles: {
        component: RecommendedArticles,
    },
    paragraph__city_block: { component: CityBlock },
    paragraph__contact_us_block: { component: ContactUs },
    paragraph__case_study_metrics: {
        component: DataCards,
        dataTransformer: (node) =>
            ({
                darkTheme: node.field_dark_mode,
                cards: node.relationships?.field_case_study_metric?.map(
                    (item) => ({
                        title: item.field_heading,
                        value: item.field_subtitle,
                        showArrow: item.field_show_arrow,
                        footnoteIndex: item.field_footnote_index,
                    })
                ),
            } as DataCardsProps),
    },
    paragraph__newsletter_signup: {
        component: NewsLetterSignUp,
        dataTransformer: (node) => ({
            heading: node.field_heading,
            body: node.field_content_text?.value,
        }),
    },
    paragraph__components_anchor: {
        component: ({ node }) => (
            <div className="anchor-target" id={node.field_heading}></div>
        ),
    },
    paragraph__two_column_dynamic_form: {
        component: TwoColumnDynamicForm,
        dataTransformer: twoColumnDynamicFormTransformer,
    },
    paragraph__two_column_form_text: {
        component: TwoColumnFormText,
        dataTransformer: (node) =>
            ({
                theme: node.field_theme,
                embeddedCode: node.field_embed_code,
                contextCol: {
                    eyebrow:
                        node.relationships?.field_form_context_column
                            ?.field_eyebrow_text,
                    heading:
                        node.relationships?.field_form_context_column
                            ?.field_heading,
                    headingOption:
                        node.relationships?.field_form_context_column
                            ?.field_heading_option,
                    contextCopy:
                        node.relationships?.field_form_context_column
                            ?.field_body_copy_content?.value,
                    contextMediaData: {
                        contextMedia:
                            node.relationships?.field_form_context_column
                                ?.relationships?.field_media_content
                                ?.relationships?.field_media_image?.localFile,
                        contextMediaMobile:
                            node.relationships?.field_form_context_column
                                ?.field_mobile_image_display,
                        contextMediaAlt:
                            node.relationships?.field_form_context_column
                                ?.relationships?.field_media_content
                                ?.field_media_image?.alt,
                    },
                    formIcons:
                        node.relationships?.field_form_context_column?.relationships?.field_form_icons?.map(
                            (icon) => {
                                if (
                                    icon.field_content_text?.value ||
                                    icon.relationships?.field_icon_img
                                ) {
                                    return {
                                        iconAltTag: icon.field_alt_tag,
                                        iconText:
                                            icon.field_content_text?.value,
                                        iconImg: icon.relationships
                                            ?.field_icon_img
                                            ? {
                                                  blueLine: getImage(
                                                      icon.relationships
                                                          ?.field_icon_img
                                                          ?.relationships
                                                          ?.field_image_dark_blue_line
                                                          ?.localFile
                                                  ),
                                                  whiteSticker: getImage(
                                                      icon.relationships
                                                          ?.field_icon_img
                                                          ?.relationships
                                                          ?.field_image_white_sticker
                                                          ?.localFile
                                                  ),
                                                  blueSticker: getImage(
                                                      icon.relationships
                                                          ?.field_icon_img
                                                          ?.relationships
                                                          ?.field_image_dark_blue_sticker
                                                          ?.localFile
                                                  ),
                                              }
                                            : undefined,
                                    };
                                } else return {};
                            }
                        ),
                    formQuotes:
                        node.relationships?.field_form_context_column?.relationships?.field_form_quote?.map(
                            (quote) => {
                                if (quote.field_content_text?.value) {
                                    return {
                                        quoteText:
                                            quote.field_content_text?.value,
                                        speaker: quote.field_quote_speaker,
                                    };
                                } else return {};
                            }
                        ),
                    conclusion:
                        node.relationships?.field_form_context_column
                            ?.field_content_text?.value,
                } as ITwoColumnFormTextContext,
            } as ITwoColumnFormText),
    },
    paragraph__two_column_text_image: {
        component: TwoColumnTextImage,
    },
    paragraph__two_column_text_media: {
        component: TwoColumnTextWMedia,
    },
};
